import moment from 'moment'
import type { ComponentProps } from 'react'
import * as React from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { calendarScrollerStyles } from 'tripPlanner/components/Calendar/calendarPanelLayout'

import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import { clampCalendarFocusMonth, type CalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlexibleDatePicker from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import FormattedSearchDate from 'components/Search/SearchForm/SearchDateInput/FormattedSearchDate'
import SearchDateToggle from 'components/Search/SearchForm/SearchDateInput/SearchDateToggle'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { addDays, addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import { getCalendarMinDate } from 'tripPlanner/utils/calendarMinDate'
import { mapFlexibleDatesToTypeaheadFormat } from 'lib/search/searchUtils'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import type { TripStop } from 'tripPlanner/api/types'

import TripContextSearchCalendar from 'tripPlanner/components/Calendar/TripContextSearchCalendar'

/** Longest stay the hotel search allows once a check-in is chosen. */
const MAX_STAY_NIGHTS = 31
/** How far ahead the calendar can be paged. */
const CALENDAR_MONTHS_AHEAD = 36

const FullWidth = styled.div`
  width: 100%;
  min-width: 0;
`

/** The panel's own chrome: what is kept for the toggle, footer and padding. */
const PANEL_CHROME_HEIGHT_PX = 190

const CalendarScroller = styled.div`
  ${calendarScrollerStyles(PANEL_CHROME_HEIGHT_PX)}
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef' | 'open' | 'portaled'> {
  /**
   * `shouldPerformSearch` runs the search immediately, and has to be passed
   * explicitly rather than wired straight to `onClick`.
   */
  onDatesApply: (shouldPerformSearch?: boolean) => void
  onDatesDecline: () => void
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  durationOptionsCustom?: Array<DurationOption>
  isSpecificSearchEnabled?: boolean
  showFlexibleYearSelector?: boolean
  closeMenu?: () => void
  disableFlexible?: boolean
  /** Called when the traveller changes dates themselves, so extracted dates stop overriding edits. */
  onUserDatesChanged?: (dates?: App.TypeaheadExtractedTravelDates) => void
  /** Month the calendar opens on before a check-in is picked. Viewport only. */
  focusMonth?: CalendarFocusMonth
  /** The trip, drawn behind the dates as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this search belongs to, so its bar can hold the foreground. */
  contextStopId?: number
}

/**
 * The hotel search date dropdown, with the trip drawn behind the calendar.
 *
 * Deliberately a trip-planner-owned copy of the standard dropdown's shell:
 * the toggle, the flexible picker and the footer come from the shared search
 * components untouched, and only the specific-dates calendar is swapped for
 * one that can show the rest of the itinerary.
 */
function TripContextSearchDateDropdown(props: Props) {
  const {
    anchorRef,
    triggerRef,
    open,
    portaled,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    durationOptionsCustom,
    isSpecificSearchEnabled = true,
    showFlexibleYearSelector,
    onUserDatesChanged,
    closeMenu,
    disableFlexible,
    focusMonth,
    tripStops,
    contextStopId,
  } = props

  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } =
    useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const intl = useIntl()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const today = useMemo(() => beginningOfToday(), [])
  /*
   * A trip already under way starts before today. Clamped to today the past
   * stops are not merely greyed out -- `from` decides where buildCalendarMonths
   * begins, so their months are never generated and the traveller loses the
   * part of the itinerary they are currently in. Kept separate from `today`,
   * which still anchors the forward extent.
   */
  const calendarMinDate = useMemo(() => {
    const earliest = (tripStops ?? [])
      .map(stop => stop.startDate)
      .filter((date): date is string => !!date)
      .sort()[0]
    return getCalendarMinDate(earliest ? moment(earliest, 'YYYY-MM-DD') : undefined).toDate()
  }, [tripStops])
  const bodyRef = useRef<HTMLDivElement>(null)
  // The calendar is the one thing that scrolls, so the toggle above it stays
  // put while months run past underneath.
  const calendarScrollRef = useRef<HTMLDivElement>(null)

  const shouldShowFlexibleSearch = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ||
    (!isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  const shouldShowSpecific = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    (isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  /**
   * The calendar's extent stays fixed at the full bookable window. Unlike the
   * standard picker it does not shrink to the longest allowed stay once a
   * check-in lands, because here the extent also decides how far the chevrons
   * can page — the stay cap is expressed as a range length instead.
   */
  const calendarMaxDate = useMemo(() => addMonths(today, CALENDAR_MONTHS_AHEAD), [today])
  const verticalPickerMaxDate = useMemo(
    () => checkinDate && !checkoutDate ? addDays(checkinDate.toDate(), MAX_STAY_NIGHTS) : calendarMaxDate,
    [calendarMaxDate, checkinDate, checkoutDate],
  )

  // An existing check-in already drives the calendar, so the hint only
  // applies before the traveller has picked anything.
  const calendarFocusDate = useMemo(
    () => checkinDate ? undefined : clampCalendarFocusMonth(focusMonth, today, calendarMaxDate),
    [calendarMaxDate, checkinDate, focusMonth, today],
  )

  const handleDatesChanged = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => {
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: dates.startDate })
      globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: dates.endDate })

      // Notify on any calendar interaction so extracted dates stop overriding
      // edits, but only commit a full range once check-out is chosen — never
      // fabricate checkOut = checkIn.
      if (dates.startDate && dates.endDate) {
        onUserDatesChanged?.({
          type: 'specific',
          checkIn: dates.startDate.format(ISO_DATE_FORMAT),
          checkOut: dates.endDate.format(ISO_DATE_FORMAT),
        })
        selectSpecificDates?.()
      } else if (dates.startDate) {
        onUserDatesChanged?.()
      }
    },
    [globalSearchDispatch, selectSpecificDates, onUserDatesChanged],
  )

  const handleFlexibleNightsChanged = useCallback(
    (newFlexibleNights: FLEXIBLE_DURATION_RANGE) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: newFlexibleNights,
      })
      if (newFlexibleNights && flexibleMonths) {
        selectFlexibleDates?.()
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonths, newFlexibleNights))
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleMonths],
  )

  const handleFlexibleMonthOptionsChanged = useCallback(
    (flexibleMonthOptions: string) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
        flexibleMonths: flexibleMonthOptions,
      })
      if (flexibleMonthOptions.length > 0) {
        selectFlexibleDates?.()
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
        const currentFlexibleNights = flexibleNights || DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonthOptions, currentFlexibleNights))
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleNights],
  )

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      selectAnytimeDate?.(true)
    }
  }, [selectAnytimeDate])

  const selectAnytimeDateNoApply = useCallback(() => {
    selectAnytimeDate?.(false)
    onUserDatesChanged?.({ type: 'anytime' })
  }, [selectAnytimeDate, onUserDatesChanged])

  const onSelectSpecificDates = useCallback(() => {
    selectSpecificDates?.()
    // Switching date mode counts as user ownership of dates — dismiss soft-confirm.
    onUserDatesChanged?.()
  }, [selectSpecificDates, onUserDatesChanged])

  const onSelectFlexibleDates = useCallback(() => {
    selectFlexibleDates?.()
    onUserDatesChanged?.()
  }, [selectFlexibleDates, onUserDatesChanged])

  /*
   * No search calendar event overlay here, deliberately.
   *
   * The experiment paints its events and public holidays onto the day cells
   * themselves, which this grid has no way to draw — the days already carry
   * the trip. Activating it anyway would enrol these travellers in an
   * experiment whose treatment they cannot see, and report their applies
   * against a suggestion that was never on the screen. They sit outside it
   * instead, and the standard calendar keeps running it in full.
   */
  const handleDatesApply = useCallback(() => onDatesApply(true), [onDatesApply])

  const hasDates = !!(checkinDate && checkoutDate)
  const disableSearchButton = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && !hasDates

  return <DropdownSheet
    data-testid="search-date-dropdown"
    size="fill-anchor"
    open={open}
    portaled={portaled}
    onClose={closeMenu}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    bodyRef={bodyRef}
    primaryActionProps={{
      'data-testid': 'search-apply-button',
      children: intl.formatMessage({
        id: 'dUn1eYBZXOHncdrqjYwc',
        defaultMessage: 'Apply',
        description: 'Button text to apply selected dates in search date picker',
      }),
      disabled: disableSearchButton,
      onClick: handleDatesApply,
    }}
    secondaryActionProps={{
      'data-testid': 'search-nodates-button',
      kind: 'tertiary',
      children: intl.formatMessage({
        id: 'sF9A_o8aEaK3HKqs6QYk',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel date selection in search date picker',
      }),
      onClick: onDatesDecline,
    }}
    footerStart={<Group direction="horizontal" grow horizontalAlign="end" verticalAlign="center" fullWidth>
      <FormattedSearchDate />
    </Group>}
  >
    <VerticalSpacer gap={32} onKeyDown={onKeyDown}>
      <Group direction="horizontal" horizontalAlign="center">
        <SearchDateToggle
          onSelectAnytime={selectAnytimeDateNoApply}
          onSelectSpecific={onSelectSpecificDates}
          anytimeDateSelected={!!anytimeDateSelected}
          onSelectFlexible={onSelectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          isSpecificSearchEnabled={isSpecificSearchEnabled}
          disableFlexible={disableFlexible}
        />
      </Group>
      <Group direction="vertical" horizontalAlign="center" fullWidth>
        {shouldShowFlexibleSearch && (
          <FlexibleDatePicker
            onMonthChange={handleFlexibleMonthOptionsChanged}
            onDurationChange={handleFlexibleNightsChanged}
            previouslySelectedDuration={flexibleNights}
            previouslySelectedMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
            durationOptionsCustom={durationOptionsCustom}
            showYearSelector={showFlexibleYearSelector}
          />
        )}
        {/*
          * A phone-sized viewport has no room for two month panels, and the
          * trip's own bars are what make the narrow list worth scrolling, so
          * it scrolls the same calendar rather than falling back to one
          * without the trip on it.
          */}
        {shouldShowSpecific && isMobileScreen && (
          <FullWidth>
            {tripStops?.length ? (
              <CalendarScroller ref={calendarScrollRef}>
                <TripContextSearchCalendar
                  tripStops={tripStops}
                  contextStopId={contextStopId}
                  startDate={checkinDate}
                  endDate={checkoutDate}
                  minDate={calendarMinDate}
                  maxDate={calendarMaxDate}
                  maxNights={MAX_STAY_NIGHTS}
                  focusMonth={calendarFocusDate}
                  onDatesChange={handleDatesChanged}
                  scrollContainerRef={calendarScrollRef}
                />
              </CalendarScroller>
            ) : (
              <VerticalDateRangePicker
                onDatesChange={handleDatesChanged}
                startDate={checkinDate}
                endDate={checkoutDate}
                min={today}
                max={verticalPickerMaxDate}
                contentRef={bodyRef}
                focusMonth={calendarFocusDate}
              />
            )}
          </FullWidth>
        )}
        {shouldShowSpecific && !isMobileScreen && (
          <FullWidth>
            <CalendarScroller ref={calendarScrollRef}>
              <TripContextSearchCalendar
                tripStops={tripStops}
                contextStopId={contextStopId}
                startDate={checkinDate}
                endDate={checkoutDate}
                minDate={calendarMinDate}
                maxDate={calendarMaxDate}
                maxNights={MAX_STAY_NIGHTS}
                focusMonth={calendarFocusDate}
                onDatesChange={handleDatesChanged}
                scrollContainerRef={calendarScrollRef}
              />
            </CalendarScroller>
          </FullWidth>
        )}
      </Group>
    </VerticalSpacer>
  </DropdownSheet>
}

export default TripContextSearchDateDropdown
